import React, { useState } from 'react';
import { Flame, X, Zap, ShieldCheck, HeartCrack } from 'lucide-react';
import { motion } from 'framer-motion';
import { authFetch, API } from '../utils/api';


const RESCUE_COST = 50;

export default function StreakRescue({ user, onClose, onRescued }) {
  const [rescuing, setRescuing] = useState(false);
  const [error, setError] = useState(null);

  const streak = user?.rescuable_streak || 0;
  const aura = user?.aura || 0;
  const canAfford = aura >= RESCUE_COST;

  const handleRescue = async () => {
    setRescuing(true);
    setError(null);
    try {
      const res = await authFetch(`${API}/api/auth/streak-rescue`, {
        method: 'POST',
              });
      if (res.ok) {
        const data = await res.json();
        onRescued(data);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to rescue streak');
      }
    } catch {
      setError('Network error');
    } finally {
      setRescuing(false);
    }
  };

  const handleDecline = () => {
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="streak-rescue-overlay"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleDecline} />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="relative z-10 w-full max-w-sm"
      >
        <button
          data-testid="streak-rescue-close-btn"
          onClick={handleDecline}
          className="absolute -top-3 -right-3 z-20 w-8 h-8 bg-bg-paper border border-white/10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors shadow-lg"
        >
          <X className="w-4 h-4" />
        </button>

        <div
          className="bg-bg-paper/95 backdrop-blur-xl rounded-3xl border border-orange-500/20 shadow-2xl overflow-hidden"
          data-testid="streak-rescue-card"
        >
          {/* Header */}
          <div className="relative h-28 flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0" style={{
              background: 'linear-gradient(135deg, rgba(255,107,53,0.15) 0%, rgba(139,0,0,0.1) 50%, rgba(255,107,53,0.05) 100%)',
            }} />
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(ellipse at 50% 80%, rgba(255,107,53,0.12) 0%, transparent 60%)',
            }} />
            <div className="relative flex items-center gap-2">
              <HeartCrack className="w-8 h-8 text-orange-400" strokeWidth={1.5} />
              <div className="flex gap-0.5">
                {[...Array(3)].map((_, i) => (
                  <Flame key={i} className="w-4 h-4 text-orange-400/40" />
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6 text-center -mt-2">
            <h2 className="font-heading text-lg font-bold mb-1" data-testid="streak-rescue-title">
              your {streak}-day streak broke
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              you missed a day, but it's not too late
            </p>

            {/* Cost card */}
            <div
              className="rounded-2xl p-4 mb-4"
              style={{ background: 'rgba(255,107,53,0.04)', border: '1px solid rgba(255,107,53,0.12)' }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground/70">Streak Rescue</span>
                <div className="flex items-center gap-1">
                  <Zap className="w-3 h-3 text-amber-400" />
                  <span className="text-sm font-bold text-amber-400" data-testid="rescue-cost">{RESCUE_COST} aura</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground/70">Your aura</span>
                <span className={`text-sm font-bold ${canAfford ? 'text-green-400' : 'text-red-400'}`} data-testid="rescue-user-aura">
                  {aura}
                </span>
              </div>
              {!canAfford && (
                <p className="text-[10px] text-red-400/70 mt-2 text-left">
                  not enough aura — invite friends via your referral link to earn more
                </p>
              )}
            </div>

            {/* What you get */}
            <div className="text-left mb-5">
              <div className="flex items-start gap-2 mb-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  restores your <span className="text-foreground font-medium">{streak}-day streak</span> + today = <span className="text-orange-400 font-bold">{streak + 1} days</span>
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Flame className="w-3.5 h-3.5 text-orange-400/60 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  one-time rescue per streak break
                </p>
              </div>
            </div>

            {error && (
              <p className="text-xs text-red-400 mb-3" data-testid="rescue-error">{error}</p>
            )}

            {/* Buttons */}
            <div className="flex gap-2.5">
              <button
                onClick={handleDecline}
                data-testid="streak-rescue-decline-btn"
                className="flex-1 border border-white/10 hover:bg-white/5 text-sm font-medium py-3 rounded-2xl transition-colors text-muted-foreground"
              >
                let it go
              </button>
              <button
                onClick={handleRescue}
                disabled={!canAfford || rescuing}
                data-testid="streak-rescue-accept-btn"
                className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-bold py-3 rounded-2xl hover:opacity-90 transition-opacity disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
              >
                {rescuing ? (
                  'rescuing...'
                ) : (
                  <>
                    <Flame className="w-3.5 h-3.5" />
                    rescue it
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
